import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { CONTENT } from "../data/content";

const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
    }),
};

const Label = ({ children }) => (
    <p className="font-body text-[10px] uppercase tracking-[0.35em] text-zinc-500">{children}</p>
);

export const Resume = () => {
    const { education, experience, skills, achievements } = CONTENT.resume;

    return (
        <section id="resume" className="border-t border-white/10 px-6 py-28 md:px-12 md:py-40" data-testid="resume-section">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={0}
                className="mb-16 flex flex-col gap-8 md:mb-24 md:flex-row md:items-end md:justify-between"
            >
                <h2 className="font-display text-6xl leading-none text-white md:text-8xl">
                    Resu<span className="text-outline">me</span>
                </h2>
                <motion.a
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.96 }}
                    href="/logan-pompa-resume.pdf"
                    download
                    data-testid="resume-download-button"
                    className="group flex w-fit items-center gap-4 border border-white/30 px-7 py-4 transition-colors duration-500 hover:border-[#E50914] hover:bg-[#E50914]"
                >
                    <Download className="h-4 w-4" />
                    <span className="font-body text-xs uppercase tracking-[0.35em]">Download PDF</span>
                </motion.a>
            </motion.div>

            <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-8">
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={0.1}
                    className="md:col-span-4"
                    data-testid="resume-education"
                >
                    <Label>Education</Label>
                    {education.map((entry) => (
                        <div key={entry.school} className="mt-6 border-t border-white/10 pt-6">
                            <h3 className="font-display text-2xl tracking-wide text-white">{entry.school}</h3>
                            <p className="mt-2 font-body text-sm text-zinc-300">{entry.degree}</p>
                            <p className="mt-1 font-body text-xs text-zinc-500">{entry.detail}</p>
                        </div>
                    ))}
                    <div className="mt-12">
                        <Label>Achievements</Label>
                        {achievements.map((item) => (
                            <div key={item.title} className="mt-6 border-t border-white/10 pt-6">
                                <h4 className="font-display text-xl tracking-wide text-white">{item.title}</h4>
                                <p className="mt-1 font-body text-xs text-zinc-500">{item.detail}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={0.2}
                    className="md:col-span-4"
                    data-testid="resume-experience"
                >
                    <Label>Professional Experience</Label>
                    {experience.map((job) => (
                        <div key={job.project} className="mt-6 border-t border-white/10 pt-6">
                            <h3 className="font-display text-2xl tracking-wide text-white">{job.role}</h3>
                            <p className="mt-1 font-body text-sm text-zinc-400">{job.project}</p>
                        </div>
                    ))}
                </motion.div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={0.3}
                    className="md:col-span-4"
                    data-testid="resume-skills"
                >
                    <Label>Skills</Label>
                    <div className="mt-6 flex flex-wrap gap-2">
                        {skills.map((skill) => (
                            <span
                                key={skill}
                                className="border border-white/15 px-4 py-2 font-body text-xs uppercase tracking-[0.15em] text-zinc-300 transition-colors duration-300 hover:border-[#E50914] hover:text-white"
                            >
                                {skill}
                            </span>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
